import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ArrowLeft, Truck, Calendar, User, Download, Loader2, CheckCircle2, XCircle,
  AlertTriangle, Thermometer, PackageCheck, Camera, FileText, PhoneCall, ClipboardCheck
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import InvoiceReconciliationTab from '../components/InvoiceReconciliationTab';

const LINE_STATUS_BADGES = {
  conforme: { cls: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-100', label: '✓ Conforme' },
  reliquat: { cls: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100', label: '⏳ Reliquat' },
  annule: { cls: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-100', label: '❌ Annulé' },
  surplus_accepte: { cls: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-100', label: '➕ Surplus accepté' },
  surplus_refuse: { cls: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-100', label: '↩️ Surplus refusé' },
  hors_commande: { cls: 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-100', label: '📞 Hors commande' },
};

const LineStatusBadge = ({ status }) => {
  const b = LINE_STATUS_BADGES[status] || LINE_STATUS_BADGES.conforme;
  return <Badge className={b.cls}>{b.label}</Badge>;
};

const ReceptionDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const loadOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      toast.error('Livraison introuvable');
      navigate('/deliveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadReceptionPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/orders/${orderId}/reception-pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RECEPTION-${order.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }
  if (!order) return null;

  const reception = order.reception || {};
  const photos = reception.photos || [];
  const horsCommande = order.hors_commande_items || [];
  const customProducts = order.custom_products || [];
  const reconciliation = order.invoice_reconciliation;
  const allLines = [
    ...order.items.map(i => ({ ...i, _kind: 'item' })),
    ...customProducts.map(cp => ({
      ...cp,
      code: '',
      article: `${cp.name} (hors catalogue)`,
      _kind: 'custom',
    })),
    ...horsCommande.map(h => ({ ...h, quantity: null, _kind: 'hors_commande', line_status: 'hors_commande' })),
  ];

  return (
    <div className="space-y-5 animate-slide-in" data-testid="reception-detail-page">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/deliveries')} className="mb-2 -ml-2 text-gray-600">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux livraisons
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{order.order_number}</h1>
            {order.has_discrepancies ? (
              <Badge className="bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-100" data-testid="discrepancies-badge">
                <AlertTriangle className="w-3 h-3 mr-1" /> Écarts à la réception
              </Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-green-300 hover:bg-green-100" data-testid="conforme-badge">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Réception conforme
              </Badge>
            )}
            {reconciliation && (
              <Badge className={reconciliation.status === 'rapproche'
                ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-100'
                : 'bg-red-100 text-red-700 border-red-300 hover:bg-red-100'}
                data-testid="reconciliation-badge"
              >
                <FileText className="w-3 h-3 mr-1" />
                {reconciliation.status === 'rapproche' ? 'Facture rapprochée' : `Facture : ${reconciliation.summary?.ecarts + reconciliation.summary?.orphelines || ''} écart(s)`}
              </Badge>
            )}
          </div>
          <Button variant="outline" onClick={downloadReceptionPDF} disabled={downloading} data-testid="download-reception-pdf">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF de réception
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Truck className="w-4 h-4" /> {order.supplier_name}</span>
          {order.received_at && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" /> Pointée le {format(new Date(order.received_at), "d MMMM 'à' HH:mm", { locale: fr })}
            </span>
          )}
          {order.received_by && (
            <span className="flex items-center gap-1"><User className="w-4 h-4" /> {order.received_by}</span>
          )}
        </div>
      </div>

      <Tabs defaultValue="reception" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="reception" data-testid="tab-reception">
            <ClipboardCheck className="w-4 h-4 mr-2" /> Réception
          </TabsTrigger>
          <TabsTrigger value="facture" data-testid="tab-facture">
            <FileText className="w-4 h-4 mr-2" /> Facture & Écarts
            {reconciliation && reconciliation.status !== 'rapproche' && (
              <span className="ml-2 w-2 h-2 rounded-full bg-red-500 inline-block" />
            )}
          </TabsTrigger>
        </TabsList>

        {/* ============ ONGLET RÉCEPTION ============ */}
        <TabsContent value="reception" className="space-y-4 mt-4">
          {/* Contrôles globaux */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${reception.temperature_ok !== false ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'}`}>
              <Thermometer className={`w-5 h-5 ${reception.temperature_ok !== false ? 'text-green-600' : 'text-red-600'}`} />
              <span className="text-sm font-medium">Température des denrées :</span>
              {reception.temperature_ok !== false
                ? <span className="text-green-700 text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Conforme</span>
                : <span className="text-red-700 text-sm font-semibold flex items-center gap-1"><XCircle className="w-4 h-4" /> Non conforme</span>}
            </div>
            <div className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${reception.packaging_ok !== false ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'}`}>
              <PackageCheck className={`w-5 h-5 ${reception.packaging_ok !== false ? 'text-green-600' : 'text-red-600'}`} />
              <span className="text-sm font-medium">Intégrité du conditionnement :</span>
              {reception.packaging_ok !== false
                ? <span className="text-green-700 text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Conforme</span>
                : <span className="text-red-700 text-sm font-semibold flex items-center gap-1"><XCircle className="w-4 h-4" /> Non conforme</span>}
            </div>
          </div>

          {/* Lignes */}
          <Card className="border border-gray-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Article</TableHead>
                    <TableHead className="text-center">Commandé</TableHead>
                    <TableHead className="text-center">Reçu</TableHead>
                    <TableHead className="text-center">Facturable</TableHead>
                    <TableHead className="text-center">Statut</TableHead>
                    <TableHead className="text-center">DLC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allLines.map((line, idx) => {
                    const isException = line.line_status && line.line_status !== 'conforme';
                    return (
                      <TableRow key={idx} className={isException ? 'bg-orange-50/50' : ''} data-testid={`detail-line-${line.code || idx}`}>
                        <TableCell>
                          {line.code && <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1 rounded mr-1">{line.code}</span>}
                          <span className="text-sm font-medium">{line.article}</span>
                          {line.reception_note && <p className="text-xs text-gray-500 italic">{line.reception_note}</p>}
                        </TableCell>
                        <TableCell className="text-center">{line._kind === 'hors_commande' ? '—' : line.quantity}</TableCell>
                        <TableCell className="text-center font-semibold">{line.quantity_received ?? line.quantity}</TableCell>
                        <TableCell className="text-center font-semibold text-emerald-700">
                          {line.billable_quantity ?? line.quantity_received ?? line.quantity}
                        </TableCell>
                        <TableCell className="text-center">
                          <LineStatusBadge status={line.line_status || 'conforme'} />
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {line.dlc ? (
                            <span className="text-amber-700 font-medium">{format(new Date(line.dlc), 'd MMM yyyy', { locale: fr })}</span>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Photos + Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {photos.length > 0 && (
              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Photos ({photos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {photos.map((p, idx) => (
                    <a key={idx} href={p} target="_blank" rel="noreferrer">
                      <img src={p} alt={`photo ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border hover:opacity-80" />
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}
            {order.reception_notes && (
              <Card className="border border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Commentaire</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.reception_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {horsCommande.length > 0 && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <PhoneCall className="w-3 h-3" /> {horsCommande.length} article(s) reçu(s) hors commande (ajout téléphonique) — inclus dans le facturable.
            </p>
          )}
        </TabsContent>

        {/* ============ ONGLET FACTURE & ÉCARTS ============ */}
        <TabsContent value="facture" className="mt-4">
          <InvoiceReconciliationTab order={order} onUpdated={loadOrder} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReceptionDetailPage;
